import { useEffect, useState } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IRequestModel } from "@interfaces/reports/reports-common.interfaces";
import { ITableColumn, IOneDrive } from "@interfaces/common.interfaces";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import ReportSection from "components/reports-common/report-section/report-section";
import { IStorageInOneDriveReportDetails, IStorageInOneDriveReportItem } from "@interfaces/reports/storage-reports.interfaces";
import SizeCell from "components/reports-common/report-results-grid-cells/size-cell/size-cell";
import StoragePercentageCell from "components/reports-common/report-results-grid-cells/storage-percentage-cell/storage-percentage-cell";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import LinkCell from "components/reports-common/report-results-grid-cells/link-cell/link-cell";
import StorageInOneDriveReportSummary from "components/reports/storage/storage-in-onedrive-report/storage-in-onedrive-report-summary";
import OneDrivePicker from "components/controls/onedrive-picker/onedrive-picker";
import { usePPA } from "context/ppa-context";
import { PPAPermissions } from "lib/permissions";

const reportUrl: string = "/api/reports/storage-in-onedrive";

export default function StorageInOneDriveReport() {
    const { currentUser, hasPermission } = usePPA();
    const { t } = useL10n<ILocaleStrings>();
    const [model, setModel] = useState<IRequestModel>({});
    const [selectedOneDrive, setSelectedOneDrive] = useState<IOneDrive>();
    const [mineOnly, setMineOnly] = useState<boolean>(false);


    const cols: Array<ITableColumn<IStorageInOneDriveReportItem>> = [
        {
            key: "folderName", 
            label: t("ppaLibrary"), 
            showInGrid: true, 
            showInExport: true, 
            defaultWidth: 200,
            renderCell: (key: string, item: IStorageInOneDriveReportItem) => 
                (<LinkCell label={item.folderName} url={item.folderUrl} />)
        },
        { 
            key: "folderUrl", 
            label: t("ppaLibraryUrl"), 
            showInGrid: false, 
            showInExport: true 
        },
        { 
            key: "folderId", 
            label: t("ppaFolderId"), 
            showInGrid: false, 
            showInExport: true 
        },
        {
            key: "totalSize", 
            label: t("ppaTotalSize"), 
            showInGrid: true, 
            showInExport: false, 
            defaultWidth: 200,
            renderCell: (key: string, item: IStorageInOneDriveReportItem) => 
                (<SizeCell value={item.totalSize} valueUnits="B" />)
        },
        { 
            key: "totalSize", 
            label: t("ppaTotalSizeBytes"), 
            showInGrid: false, 
            showInExport: true 
        },
        {
            key: "percentageOfSiteUsage", 
            label: t("ppaPercentageOfSiteUsage"), 
            showInGrid: true, 
            showInExport: true, 
            defaultWidth: 200,
            renderCell: (key: string, item: IStorageInOneDriveReportItem, details: IStorageInOneDriveReportDetails) => (
                <StoragePercentageCell
                    percentageUsed={item.percentageOfSiteUsage}
                    usedValue={item.totalSize}
                    usedValueUnits="B"
                    maxValue={details.siteTotalSizeKB}
                    maxValueUnits="KB" />)
        },
        {
            key: "percentageOfQuota", 
            label: t("ppaPercentageOfQuota"), 
            showInGrid: true, 
            showInExport: true, 
            defaultWidth: 200,
            renderCell: (key: string, item: IStorageInOneDriveReportItem, details: IStorageInOneDriveReportDetails) => (
                <StoragePercentageCell
                    percentageUsed={item.percentageOfQuota}
                    usedValue={item.totalSize}
                    usedValueUnits="B"
                    maxValue={details.siteQuotaMaximumMB}
                    maxValueUnits="MB" />)
        },
        { 
            key: "totalFileCount", 
            label: t("ppaFileCount"), 
            showInGrid: true, 
            showInExport: true, 
            defaultWidth: 200 
        },
        {
            key: "lastModified", 
            label: t("lastModified"), 
            showInGrid: true, 
            showInExport: true,
            renderCell: (key: string, item: IStorageInOneDriveReportItem) => 
                (<DateTimeCell columnId={key} item={item} pattern="PPp" />)
        }
    ];

    const onOneDriveChange = (value?: IOneDrive) => {
        console.log(`StorageInOneDriveReport -> onOneDriveChange -> `, value);
        // When we set the model from the picker, set both the site url and the owner because site url will not be set if this was defaulted from the current user.
        setModel({ ...model, oneDrive: { siteUrl: value?.siteUrl, owner: value?.owner } });
        setSelectedOneDrive(value);
    };


    useEffect(() => {
        if (currentUser)
            // Set the selected onedrive from the current users display name and upn. We don't know the site url but that's fine as the upn is also an identifier.
            setSelectedOneDrive({ siteUrl: '', title: currentUser.displayName, owner: currentUser.upn });
        if (hasPermission(PPAPermissions.RequestStorageInOneDriveReportMineOnly) && !hasPermission(PPAPermissions.RequestStorageInOneDriveReportAny))
            setMineOnly(true);
    }, []);

    // Either site url or owner can be used as an identifier.
    const isModelValid = (): boolean => model.oneDrive?.siteUrl || model.oneDrive?.owner ? true : false;


    const reportSectionProps: IReportSectionProps<IStorageInOneDriveReportItem, IStorageInOneDriveReportDetails> = {
        columns: cols,
        summaryComponent: <StorageInOneDriveReportSummary />,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaStorageInOneDriveReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaStorageInOneDriveReportDefaultExportFilenamePrefix"
    };

    return (
        <ReportSection {...reportSectionProps}>
            <OneDrivePicker
                onChange={onOneDriveChange}
                model={selectedOneDrive}
                readonly={mineOnly} />
        </ReportSection>
    );
}